import { useState, useEffect, useCallback, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calculator, Truck, Package, Loader2, Euro, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { calcTotals, computeSharedValues } from '../../lib/priceFormula';

const API = process.env.REACT_APP_BACKEND_URL;

const FORMULE_LABELS = {
    economique: 'Économique',
    economique_plus: 'Éco. Plus',
    standard: 'Standard',
    luxe: 'Luxe',
};

const FORMULE_ORDER = ['economique', 'economique_plus', 'standard', 'luxe'];

const DEFAULT_FORMULE_DATA = () => ({
    nb_jours: '',
    effectif_jours: {},
    tarif_demenageur: '',
    panier: '',
    hotel: '',
    prix_aot_depart: '',
    prix_aot_arrivee: '',
    prix_monte_meuble_depart: '',
    prix_monte_meuble_arrivee: '',
    tarif_dechetterie: '',
    tarif_objet_lourd: '',
    marge: '',
});

// Backward compat: migrate old effectif -> effectif_jours
function migrateFormuleData(saved) {
    const d = { ...saved };
    if (d.effectif && !d.effectif_jours) {
        d.effectif_jours = { '1': d.effectif };
    }
    if (!d.effectif_jours) d.effectif_jours = {};
    delete d.effectif;
    return d;
}

export const PriceCalculator = forwardRef(function PriceCalculator({ inventaireId, formules, adresseDepart, adresseArrivee, onCompleteChange }, ref) {
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const saveRef = useRef(null);

    const [infosBase, setInfosBase] = useState({ volume_devis: '', camion_20: '', camion_30: '', distance: '' });
    const [fournituresActive, setFournituresActive] = useState(null);
    const [fournitures, setFournitures] = useState({ cartons_standards: '', cartons_livres: '', cartons_verres: '', cartons_bouteilles: '', adhesif: '', bulle_grande: '', bulle_petite: '', autres: '' });
    const [programmation, setProgrammation] = useState({});
    const [priceSettings, setPriceSettings] = useState(null);
    // Detection auto depuis le calculateur de volume (Section 9) :
    //   - presenceDechetterie : au moins 1 article type_article === 'decheterie'
    //   - presenceObjetLourd : au moins 1 article a objet_lourd === true
    const [presenceDechetterie, setPresenceDechetterie] = useState(false);
    const [presenceObjetLourd, setPresenceObjetLourd] = useState(false);
    const [formulesData, setFormulesData] = useState(() => {
        const init = {};
        (formules || []).forEach(f => { init[f] = DEFAULT_FORMULE_DATA(); });
        return init;
    });

    // Load admin price settings (formula + TVA)
    useEffect(() => {
        axios.get(`${API}/api/admin/price-settings`, { withCredentials: true })
            .then(({ data }) => setPriceSettings(data))
            .catch(() => setPriceSettings({ formula_ht: '', tva_rate: 20 }));
    }, []);

    // Compute dynamic rows based on address data
    const dynamicRows = useMemo(() => {
        const rows = [];
        const aotDepart = adresseDepart?.autorisation_stationnement === true;
        const aotArrivee = adresseArrivee?.autorisation_stationnement === true;
        const mmDepart = adresseDepart?.monte_meuble === true;
        const mmArrivee = adresseArrivee?.monte_meuble === true;

        if (aotDepart) rows.push({ key: 'prix_aot_depart', label: 'Prix AOT départ' });
        if (aotArrivee) rows.push({ key: 'prix_aot_arrivee', label: 'Prix AOT arrivée' });
        if (mmDepart) rows.push({ key: 'prix_monte_meuble_depart', label: 'Prix Monte-meuble départ' });
        if (mmArrivee) rows.push({ key: 'prix_monte_meuble_arrivee', label: 'Prix Monte-meuble arrivée' });
        if (presenceDechetterie) rows.push({ key: 'tarif_dechetterie', label: 'Tarif déchetterie' });
        if (presenceObjetLourd) rows.push({ key: 'tarif_objet_lourd', label: 'Tarif objet lourd' });

        return rows;
    }, [adresseDepart?.autorisation_stationnement, adresseArrivee?.autorisation_stationnement, adresseDepart?.monte_meuble, adresseArrivee?.monte_meuble, presenceDechetterie, presenceObjetLourd]);

    // Sync formules columns when formules prop changes (only after loaded)
    useEffect(() => {
        if (!loaded || !formules || formules.length === 0) return;
        setFormulesData(prev => {
            const next = {};
            formules.forEach(f => { next[f] = prev[f] || DEFAULT_FORMULE_DATA(); });
            return next;
        });
    }, [formules?.join(','), loaded]);

    // Load saved data on mount
    useEffect(() => {
        if (!inventaireId) { setLoaded(true); return; }
        let cancelled = false;
        setLoaded(false);
        axios.get(`${API}/api/inventaires/${inventaireId}`, { withCredentials: true })
            .then(({ data }) => {
                if (cancelled) return;
                const cp = data.calcul_prix;
                if (cp) {
                    if (cp.infos_base) setInfosBase(prev => ({ ...prev, ...cp.infos_base }));
                    if (cp.fournitures_active !== undefined) setFournituresActive(cp.fournitures_active);
                    if (cp.fournitures) setFournitures(prev => ({ ...prev, ...cp.fournitures }));
                    if (cp.formules_data) {
                        // Use formules from the loaded inventaire data, not from prop (which may be empty during mount)
                        const fList = data.formules || formules || [];
                        setFormulesData(() => {
                            const merged = {};
                            // Also keep ALL saved formula keys in case prop hasn't loaded yet
                            const allKeys = new Set([...fList, ...Object.keys(cp.formules_data)]);
                            allKeys.forEach(f => {
                                const saved = cp.formules_data[f] || DEFAULT_FORMULE_DATA();
                                merged[f] = migrateFormuleData({ ...DEFAULT_FORMULE_DATA(), ...saved });
                            });
                            return merged;
                        });
                    }
                }
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setLoaded(true); });
        return () => { cancelled = true; };
    }, [inventaireId]);

    // Load stats exploitation (all bookings by date)
    useEffect(() => {
        if (!inventaireId) return;
        axios.get(`${API}/api/inventaires/${inventaireId}/programmation`, { withCredentials: true })
            .then(({ data }) => setProgrammation(data))
            .catch(() => {});
    }, [inventaireId]);

    // Recharge la presence dechetterie / objet lourd depuis les articles du
    // calculateur de volume (Section 9). Refresh a chaque ouverture pour
    // capter les modifications faites cote VolumeCalculator.
    useEffect(() => {
        if (!inventaireId) return;
        let cancelled = false;
        const reload = () => {
            axios.get(`${API}/api/inventaires/${inventaireId}/articles`, { withCredentials: true })
                .then(({ data }) => {
                    if (cancelled) return;
                    const articles = Array.isArray(data) ? data : (data?.articles || []);
                    const hasDech = articles.some(a => (a.type_article || '') === 'decheterie' && (parseFloat(a.volume) || 0) > 0);
                    const hasLourd = articles.some(a => a.objet_lourd === true);
                    setPresenceDechetterie(hasDech);
                    setPresenceObjetLourd(hasLourd);
                })
                .catch(() => {});
        };
        reload();
        // Refresh toutes les 5s tant que la modale est ouverte
        const interval = setInterval(reload, 5000);
        return () => { cancelled = true; clearInterval(interval); };
    }, [inventaireId]);

    // Auto-save debounced
    const latestDataRef = useRef(null);
    const autoSave = useCallback((payload) => {
        if (!inventaireId) return;
        latestDataRef.current = payload;
        if (saveRef.current) clearTimeout(saveRef.current);
        saveRef.current = setTimeout(async () => {
            setSaving(true);
            try {
                await axios.put(`${API}/api/inventaires/${inventaireId}`, { calcul_prix: payload }, { withCredentials: true });
                latestDataRef.current = null;
            } catch { toast.error('Erreur de sauvegarde'); }
            finally { setSaving(false); }
        }, 500);
    }, [inventaireId]);

    // Flush pending save on unmount
    useEffect(() => {
        return () => {
            if (saveRef.current) clearTimeout(saveRef.current);
            if (latestDataRef.current && inventaireId) {
                const payload = latestDataRef.current;
                axios.put(`${API}/api/inventaires/${inventaireId}`, { calcul_prix: payload }, { withCredentials: true }).catch(() => {});
            }
        };
    }, [inventaireId]);

    // Expose `flushSave` au parent : permet de forcer (et d'attendre) la sauvegarde
    // pendante avant une action critique (PDF, fermeture du dialog, etc.) afin
    // d'éviter une race condition avec le debounce de 500 ms.
    useImperativeHandle(ref, () => ({
        flushSave: async () => {
            if (saveRef.current) {
                clearTimeout(saveRef.current);
                saveRef.current = null;
            }
            if (!latestDataRef.current || !inventaireId) return;
            const payload = latestDataRef.current;
            setSaving(true);
            try {
                await axios.put(`${API}/api/inventaires/${inventaireId}`, { calcul_prix: payload }, { withCredentials: true });
                latestDataRef.current = null;
            } finally {
                setSaving(false);
            }
        },
    }), [inventaireId]);

    const initialLoadRef = useRef(true);
    useEffect(() => {
        if (!loaded) return;
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            return;
        }
        autoSave({ infos_base: infosBase, fournitures_active: fournituresActive, fournitures, formules_data: formulesData });
    }, [infosBase, fournituresActive, fournitures, formulesData, loaded, autoSave]);

    const activeFormules = (formules || []).slice().sort((a, b) => FORMULE_ORDER.indexOf(a) - FORMULE_ORDER.indexOf(b));
    const sharedKeys = dynamicRows.map(r => r.key);
    const formulaStr = priceSettings?.formula_ht || '';
    const tvaRate = priceSettings?.tva_rate ?? 20;
    // Shared values : valeur saisie une seule fois et appliquée à toutes les formules.
    const sharedValues = computeSharedValues(formulesData, sharedKeys);
    const hasData = activeFormules.some(f => calcTotals(formulesData[f], sharedKeys, infosBase, fournitures, formulaStr, tvaRate, fournituresActive, { presenceDechetterie, presenceObjetLourd, sharedValues }).complete);

    // Notifie le parent de l'état de complétude (pour le statut global de l'inventaire)
    useEffect(() => {
        if (onCompleteChange) onCompleteChange(hasData);
    }, [hasData, onCompleteChange]);

    // Compute max jours across all active formulas for dynamic effectif rows
    const maxJours = activeFormules.reduce((max, fKey) => {
        const j = parseInt((formulesData[fKey] || {}).nb_jours) || 0;
        return Math.max(max, j);
    }, 0);

    if (!loaded) return <Card className="border-slate-200"><CardContent className="py-8 flex justify-center"><Loader2 className="animate-spin text-[#2e7cd6]" size={24} /></CardContent></Card>;

    // Row index counter for alternating bg
    let rowIdx = 0;

    // Pré-conditions pour afficher le Total TTC :
    //  - Informations de base : tous les champs remplis
    //  - Fournitures : choix Oui (tous champs remplis) ou Non (champ "autres" rempli)
    const infosBaseComplete = !!(infosBase.volume_devis && infosBase.camion_20 && infosBase.camion_30 && infosBase.distance);
    const fournituresBlocComplete = fournituresActive === false
        ? (fournitures.autres !== '' && fournitures.autres !== null && fournitures.autres !== undefined)
        : (fournituresActive === true && Object.entries(fournitures).every(([, v]) => v !== '' && v !== null && v !== undefined));

    return (
        <Card className="border-slate-200" data-testid="price-calculator">
            <CardHeader className="pb-3 border-b border-slate-100">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                    <Calculator size={16} className="text-[#2e7cd6]" /> Calcul de prix
                </CardTitle>
            </CardHeader>

            <CardContent className="pt-4 space-y-10">
                {/* Bloc 1 : Informations de base - Avec barre de statut globale */}
                {(() => {
                    const infosComplete = !!(infosBase.volume_devis && infosBase.camion_20 && infosBase.camion_30 && infosBase.distance);
                    return (
                <div className="flex gap-3">
                    <div className={`w-1 rounded-full self-stretch shrink-0 transition-colors ${hasData ? 'bg-emerald-500' : 'bg-red-400'}`} data-testid="prix-status-bar" />
                    <div className="flex-1 rounded-lg border-2 border-[#2e7cd6] overflow-hidden shadow-sm ring-1 ring-[#2e7cd6]/10">
                        <div className="bg-gradient-to-r from-[#2e7cd6] to-[#4a90d9] px-4 py-3 border-b border-[#2e7cd6]">
                            <h4 className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-2">
                                <div className={`w-1 h-5 rounded-full shrink-0 ${infosComplete ? 'bg-emerald-300' : 'bg-red-300'}`} />
                                <Truck size={14} /> Informations de base
                            </h4>
                        </div>
                        <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 bg-blue-50/50">
                            <div>
                                <Label className="text-xs font-semibold text-[#2e7cd6] uppercase tracking-wide">Volume devis</Label>
                                <Input type="number" min="0" step="any" value={infosBase.volume_devis} onChange={e => setInfosBase(p => ({ ...p, volume_devis: e.target.value }))} className="mt-1 h-10 text-sm font-semibold bg-white border-[#2e7cd6]/30 focus:border-[#2e7cd6]" data-testid="prix-volume-devis" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-[#2e7cd6] uppercase tracking-wide">Camion 20 m³</Label>
                                <Input type="number" min="0" value={infosBase.camion_20} onChange={e => setInfosBase(p => ({ ...p, camion_20: e.target.value }))} className="mt-1 h-10 text-sm font-semibold bg-white border-[#2e7cd6]/30 focus:border-[#2e7cd6]" data-testid="prix-camion-20" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-[#2e7cd6] uppercase tracking-wide">Camion 30 m³</Label>
                                <Input type="number" min="0" value={infosBase.camion_30} onChange={e => setInfosBase(p => ({ ...p, camion_30: e.target.value }))} className="mt-1 h-10 text-sm font-semibold bg-white border-[#2e7cd6]/30 focus:border-[#2e7cd6]" data-testid="prix-camion-30" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold text-[#2e7cd6] uppercase tracking-wide">Distance (km)</Label>
                                <Input type="number" min="0" value={infosBase.distance} onChange={e => setInfosBase(p => ({ ...p, distance: e.target.value }))} className="mt-1 h-10 text-sm font-semibold bg-white border-[#2e7cd6]/30 focus:border-[#2e7cd6]" data-testid="prix-distance" />
                            </div>
                        </div>
                    </div>
                </div>
                    );
                })()}
                {(() => {
                    const fournituresComplete = fournituresActive === false
                        ? !!fournitures.autres
                        : (fournituresActive === true && Object.entries(fournitures).every(([k, v]) => v !== '' && v !== null && v !== undefined));
                    return (
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className={`w-1 h-5 rounded-full shrink-0 ${fournituresComplete ? 'bg-emerald-500' : 'bg-red-400'}`} />
                                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2"><Package size={14} /> Fournitures</h4>
                            </div>
                            <Select value={fournituresActive === true ? 'oui' : fournituresActive === false ? 'non' : ''} onValueChange={v => {
                                const newActive = v === 'oui';
                                setFournituresActive(newActive);
                                // Réinitialise tous les champs fournitures quand on bascule Oui ↔ Non
                                // pour repartir d'une saisie propre (cartons, adhésif, bulles + "Autre fourniture").
                                setFournitures({ cartons_standards: '', cartons_livres: '', cartons_verres: '', cartons_bouteilles: '', adhesif: '', bulle_grande: '', bulle_petite: '', autres: '' });
                            }}>
                                <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="prix-fournitures-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="oui">Oui</SelectItem>
                                    <SelectItem value="non">Non</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {fournituresActive === true && (
                            <div className="p-4 space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[
                                        { key: 'cartons_standards', label: 'Cartons standards' },
                                        { key: 'cartons_livres', label: 'Cartons livres' },
                                        { key: 'cartons_verres', label: 'Cartons verres (100 verres)' },
                                        { key: 'cartons_bouteilles', label: 'Cartons bouteilles' },
                                    ].map(f => (
                                        <div key={f.key}>
                                            <Label className="text-xs text-slate-500">{f.label}</Label>
                                            <Input type="number" min="0" value={fournitures[f.key]} onChange={e => setFournitures(p => ({ ...p, [f.key]: e.target.value }))} className="mt-1 h-9 text-sm" data-testid={`prix-${f.key}`} />
                                        </div>
                                    ))}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    {[
                                        { key: 'adhesif', label: 'Adhésif' },
                                        { key: 'bulle_grande', label: 'Bulle (100m x 50cm)' },
                                        { key: 'bulle_petite', label: 'Bulle (10m x 1m)' },
                                    ].map(f => (
                                        <div key={f.key}>
                                            <Label className="text-xs text-slate-500">{f.label}</Label>
                                            <Input type="number" min="0" value={fournitures[f.key]} onChange={e => setFournitures(p => ({ ...p, [f.key]: e.target.value }))} className="mt-1 h-9 text-sm" data-testid={`prix-${f.key}`} />
                                        </div>
                                    ))}
                                    <div>
                                        <Label className="text-xs text-slate-500">Autre fourniture</Label>
                                        <Input type="number" min="0" value={fournitures.autres} onChange={e => setFournitures(p => ({ ...p, autres: e.target.value }))} className="mt-1 h-9 text-sm" data-testid="prix-autres-fournitures" />
                                    </div>
                                </div>
                            </div>
                        )}
                        {fournituresActive === false && (
                            <div className="p-4">
                                <Label className="text-xs text-slate-500">Autre fourniture</Label>
                                <Input type="number" min="0" value={fournitures.autres} onChange={e => setFournitures(p => ({ ...p, autres: e.target.value }))} className="mt-1 h-9 text-sm max-w-[200px]" data-testid="prix-autres-fournitures-non" />
                            </div>
                        )}
                    </div>
                    );
                })()}

                {/* Bloc Statistiques exploitation */}
                {(() => {
                    const rawDate = adresseDepart?.date || '';
                    if (!rawDate) return null;
                    const startDate = new Date(rawDate);
                    if (isNaN(startDate.getTime())) return null;
                    
                    const maxJ = Math.max(1, ...activeFormules.map(fk => parseInt((formulesData[fk] || {}).nb_jours) || 0));
                    
                    const rows = [];
                    for (let i = 0; i < maxJ; i++) {
                        const d = new Date(startDate.getTime() + i * 86400000);
                        const dateKey = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                        const dateDisplay = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
                        const stats = programmation[dateKey] || {};
                        rows.push({ jour: i + 1, date: dateDisplay, dem: stats.nb_demenageurs || 0, c20: stats.camion_20m3 || 0, c30: stats.camion_30m3 || 0 });
                    }
                    
                    return (
                        <div className="rounded-lg border border-slate-200 overflow-hidden">
                            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
                                <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                                    <CalendarDays size={14} className="text-[#2e7cd6]" /> Statistiques exploitation
                                </h4>
                                <p className="text-[10px] text-slate-400 mt-1">Ressources déjà programmées sur ces dates (tous clients confondus)</p>
                            </div>
                            <div className="p-4">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm border border-slate-200 rounded-lg" data-testid="stats-exploitation-table">
                                        <thead>
                                            <tr className="bg-slate-50 border-b border-slate-200">
                                                <th className="text-left px-4 py-2 text-xs font-bold text-slate-500 uppercase">Jour</th>
                                                <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Date</th>
                                                <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Déménageurs</th>
                                                <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Camion 20m³</th>
                                                <th className="text-center px-4 py-2 text-xs font-bold text-slate-500 uppercase">Camion 30m³</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.map((r, i) => {
                                                const hasBookings = r.dem > 0 || r.c20 > 0 || r.c30 > 0;
                                                return (
                                                    <tr key={i} className={hasBookings ? 'bg-amber-50/50' : 'bg-white'}>
                                                        <td className="px-4 py-2 font-medium text-slate-700 border-b border-slate-100">Jour {r.jour}</td>
                                                        <td className="px-4 py-2 text-center text-slate-700 border-b border-slate-100">{r.date}</td>
                                                        <td className={`px-4 py-2 text-center font-semibold border-b border-slate-100 ${r.dem > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{r.dem}</td>
                                                        <td className={`px-4 py-2 text-center font-semibold border-b border-slate-100 ${r.c20 > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{r.c20}</td>
                                                        <td className={`px-4 py-2 text-center font-semibold border-b border-slate-100 ${r.c30 > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{r.c30}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Bloc 3 : Calcul des couts */}
                {activeFormules.length > 0 ? (
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                        <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
                            <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                                <div className={`w-1 h-5 rounded-full shrink-0 ${hasData ? 'bg-emerald-500' : 'bg-red-400'}`} />
                                <Euro size={14} /> Calcul des coûts
                            </h4>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="prix-couts-table">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50">
                                        <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase tracking-wider w-[160px]">Ligne</th>
                                        {activeFormules.map(fKey => (
                                            <th key={fKey} className="text-center px-3 py-2.5 text-xs font-bold text-blue-600 uppercase tracking-wider">{FORMULE_LABELS[fKey] || fKey}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Row: Nombre de jours */}
                                    <tr className="bg-white">
                                        <td className="px-4 py-2 text-xs font-medium text-slate-600 border-r border-slate-100">Nombre de jours</td>
                                        {activeFormules.map(fKey => (
                                            <td key={fKey} className="px-2 py-1.5 text-center">
                                                <Input type="number" min="0" step="1"
                                                    value={(formulesData[fKey] || {}).nb_jours || ''}
                                                    onChange={e => setFormulesData(prev => ({ ...prev, [fKey]: { ...(prev[fKey] || DEFAULT_FORMULE_DATA()), nb_jours: e.target.value } }))}
                                                    className="h-8 text-sm text-center w-full" data-testid={`prix-${fKey}-nb_jours`} />
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Dynamic Effectif jr X rows */}
                                    {Array.from({ length: maxJours }, (_, idx) => idx + 1).map(dayNum => (
                                        <tr key={`eff-${dayNum}`} className={dayNum % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}>
                                            <td className="px-4 py-2 text-xs font-medium text-blue-600 border-r border-slate-100">
                                                Effectif jr {dayNum}
                                            </td>
                                            {activeFormules.map(fKey => {
                                                const fData = formulesData[fKey] || {};
                                                const nbJ = parseInt(fData.nb_jours) || 0;
                                                const isActive = dayNum <= nbJ;
                                                return (
                                                    <td key={fKey} className="px-2 py-1.5 text-center">
                                                        {isActive ? (
                                                            <Input type="text"
                                                                value={(fData.effectif_jours || {})[String(dayNum)] || ''}
                                                                onChange={e => setFormulesData(prev => ({
                                                                    ...prev,
                                                                    [fKey]: {
                                                                        ...(prev[fKey] || DEFAULT_FORMULE_DATA()),
                                                                        effectif_jours: {
                                                                            ...(prev[fKey]?.effectif_jours || {}),
                                                                            [String(dayNum)]: e.target.value
                                                                        }
                                                                    }
                                                                }))}
                                                                className="h-8 text-sm text-center w-full" data-testid={`prix-${fKey}-effectif-jr${dayNum}`} />
                                                        ) : (
                                                            <span className="text-slate-300 text-xs">-</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}

                                    {/* Tarif demenageur */}
                                    {(() => { rowIdx = maxJours + 1; return null; })()}
                                    <tr className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                        <td className="px-4 py-2 text-xs font-medium text-slate-600 border-r border-slate-100">Tarif déménageur (€)</td>
                                        {activeFormules.map(fKey => (
                                            <td key={fKey} className="px-2 py-1.5 text-center">
                                                <Input type="number" min="0" step="any"
                                                    value={(formulesData[fKey] || {}).tarif_demenageur || ''}
                                                    onChange={e => setFormulesData(prev => ({ ...prev, [fKey]: { ...(prev[fKey] || DEFAULT_FORMULE_DATA()), tarif_demenageur: e.target.value } }))}
                                                    className="h-8 text-sm text-center w-full" data-testid={`prix-${fKey}-tarif_demenageur`} />
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Panier */}
                                    {(() => { rowIdx++; return null; })()}
                                    <tr className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                        <td className="px-4 py-2 text-xs font-medium text-slate-600 border-r border-slate-100">Panier (€)</td>
                                        {activeFormules.map(fKey => (
                                            <td key={fKey} className="px-2 py-1.5 text-center">
                                                <Input type="number" min="0" step="any"
                                                    value={(formulesData[fKey] || {}).panier || ''}
                                                    onChange={e => setFormulesData(prev => ({ ...prev, [fKey]: { ...(prev[fKey] || DEFAULT_FORMULE_DATA()), panier: e.target.value } }))}
                                                    className="h-8 text-sm text-center w-full" data-testid={`prix-${fKey}-panier`} />
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Hotel */}
                                    {(() => { rowIdx++; return null; })()}
                                    <tr className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                                        <td className="px-4 py-2 text-xs font-medium text-slate-600 border-r border-slate-100">Hôtel (€)</td>
                                        {activeFormules.map(fKey => (
                                            <td key={fKey} className="px-2 py-1.5 text-center">
                                                <Input type="number" min="0" step="any"
                                                    value={(formulesData[fKey] || {}).hotel || ''}
                                                    onChange={e => setFormulesData(prev => ({ ...prev, [fKey]: { ...(prev[fKey] || DEFAULT_FORMULE_DATA()), hotel: e.target.value } }))}
                                                    className="h-8 text-sm text-center w-full" data-testid={`prix-${fKey}-hotel`} />
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Dynamic AOT / Monte-meuble rows — shared across all formulas */}
                                    {dynamicRows.map(row => {
                                        rowIdx++;
                                        // Get the shared value from any formula that has it
                                        const sharedVal = activeFormules.reduce((v, fk) => v || (formulesData[fk] || {})[row.key] || '', '');
                                        return (
                                            <tr key={row.key} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-amber-50/40'}>
                                                <td className="px-4 py-2 text-xs font-medium text-amber-700 border-r border-slate-100">{row.label}</td>
                                                <td colSpan={activeFormules.length} className="px-2 py-1.5 text-center">
                                                    <Input type="number" min="0" step="any"
                                                        value={sharedVal}
                                                        onChange={e => {
                                                            const val = e.target.value;
                                                            setFormulesData(prev => {
                                                                const next = { ...prev };
                                                                activeFormules.forEach(fk => {
                                                                    next[fk] = { ...(next[fk] || DEFAULT_FORMULE_DATA()), [row.key]: val };
                                                                });
                                                                return next;
                                                            });
                                                        }}
                                                        className="h-8 text-sm text-center max-w-[200px] mx-auto" data-testid={`prix-shared-${row.key}`} />
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {/* Marge */}
                                    {(() => { rowIdx++; return null; })()}
                                    <tr className="bg-emerald-50/60 border-t border-emerald-200">
                                        <td className="px-4 py-2.5 text-xs font-bold text-emerald-700 border-r border-emerald-200">Marge (€)</td>
                                        {activeFormules.map(fKey => (
                                            <td key={fKey} className="px-2 py-1.5 text-center">
                                                <Input type="number" min="0" step="any"
                                                    value={(formulesData[fKey] || {}).marge || ''}
                                                    onChange={e => setFormulesData(prev => ({ ...prev, [fKey]: { ...(prev[fKey] || DEFAULT_FORMULE_DATA()), marge: e.target.value } }))}
                                                    className="h-8 text-sm text-center w-full font-semibold border-emerald-300 focus:border-emerald-500" data-testid={`prix-${fKey}-marge`} />
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Total HT */}
                                    <tr className="border-t-2 border-slate-300 bg-blue-50/50">
                                        <td className="px-4 py-2.5 text-xs font-bold text-slate-700 border-r border-slate-200">Total HT</td>
                                        {activeFormules.map(fKey => (
                                            <td key={fKey} className="px-3 py-2.5 text-center font-bold text-slate-700" data-testid={`prix-ht-${fKey}`}>
                                                {calcTotals(formulesData[fKey], sharedKeys, infosBase, fournitures, formulaStr, tvaRate, fournituresActive, { presenceDechetterie, presenceObjetLourd, sharedValues }).ht.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Total TTC */}
                                    <tr className="bg-blue-100/50">
                                        <td className="px-4 py-2.5 text-xs font-bold text-blue-700 border-r border-slate-200">Total TTC</td>
                                        {activeFormules.map(fKey => {
                                            const totals = calcTotals(formulesData[fKey], sharedKeys, infosBase, fournitures, formulaStr, tvaRate, fournituresActive, { presenceDechetterie, presenceObjetLourd, sharedValues });
                                            const showTtc = infosBaseComplete && fournituresBlocComplete && totals.complete;
                                            return (
                                                <td key={fKey} className="px-3 py-2.5 text-center font-extrabold text-blue-700 text-base" data-testid={`prix-ttc-${fKey}`}>
                                                    {showTtc
                                                        ? `${totals.ttc.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`
                                                        : <span className="text-slate-300 font-semibold">—</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
                        Sélectionnez des formules dans la section "Formules" pour calculer les prix
                    </div>
                )}

                {saving && (
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                        <Loader2 size={12} className="animate-spin" /> Sauvegarde...
                    </div>
                )}
            </CardContent>
        </Card>
    );
});
